// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: picking a time for a mail that asks to agree one (design/draft-reply-slots, 2026-10-22).
// The summary's meeting action carries the times worth offering per length, worked out on the
// server from the reader's calendar and those of the colleagues on the mail
// (konvo_api KonvoMailMeeting). The Konvo card shows them as chips; the reply opens with the
// chosen time written in and a "When" line in the composer to change it, and when the reply is
// sent a Teams invite can go to everyone on it. This module is the pure part plus the invite.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {KonvoMailAddress} from '@app/features/konvo_mail/state/KonvoMail';
import {http} from '@app/features/platform/transport/RestTransport';

export interface MeetingOption {
	start: string;
	end: string;
	/** Indexes into `people` of those busy then. */
	busy: Array<number>;
}

export interface MeetingPerson {
	name: string;
	/** For the avatar ("BM"); servers before it sent none. */
	initials?: string;
	address: string;
	self: boolean;
	/** False when their calendar could not be read. */
	known: boolean;
}

export interface MailMeetingAction {
	kind: 'meeting';
	topic: string;
	/** The sender offered the times (the reply accepts one); false when Konvo proposes them. */
	offered: boolean;
	duration_minutes: number;
	durations: Array<number>;
	lang: string;
	people: Array<MeetingPerson>;
	options: Record<string, Array<MeetingOption>>;
	unavailable: boolean;
}

/** What the reader picked: a length, a time, a fallback and whether an invite goes out. */
export interface MeetingChoice {
	duration: number;
	start: string | null;
	backup: string | null;
	invite: boolean;
}

/** The composer's copy: the action, the choice and the time texts it wrote into the reply. */
export interface ComposeMeeting {
	action: MailMeetingAction;
	choice: MeetingChoice;
	texts: {time: string | null; backup: string | null};
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	value != null && typeof value === 'object' && !Array.isArray(value);

/** The server's meeting action, or null when it is missing or malformed. */
export function parseMeetingAction(value: unknown): MailMeetingAction | null {
	if (!isRecord(value) || value.kind !== 'meeting' || !isRecord(value.options) || !Array.isArray(value.people)) {
		return null;
	}
	const people = Array.isArray(value.people)
		? value.people.filter(
				(person): person is MeetingPerson =>
					isRecord(person) && typeof person.name === 'string' && typeof person.address === 'string',
			)
		: [];
	const options: Record<string, Array<MeetingOption>> = {};
	if (isRecord(value.options)) {
		for (const [minutes, list] of Object.entries(value.options)) {
			if (!Array.isArray(list)) continue;
			options[minutes] = list.filter(
				(option): option is MeetingOption =>
					isRecord(option) &&
					typeof option.start === 'string' &&
					typeof option.end === 'string' &&
					Array.isArray(option.busy),
			);
		}
	}
	const durations = Array.isArray(value.durations)
		? value.durations.filter((minutes): minutes is number => typeof minutes === 'number' && minutes > 0)
		: [];
	return {
		kind: 'meeting',
		topic: typeof value.topic === 'string' && value.topic.trim() ? value.topic.trim() : 'Meeting',
		offered: value.offered === true,
		duration_minutes: typeof value.duration_minutes === 'number' ? value.duration_minutes : 60,
		durations: durations.length ? durations : [30, 60, 90, 120],
		lang: typeof value.lang === 'string' ? value.lang : 'en',
		people: people.map((person) => ({...person, self: person.self === true, known: person.known !== false})),
		options,
		unavailable: value.unavailable === true,
	};
}

export function optionsFor(action: MailMeetingAction, duration: number): Array<MeetingOption> {
	return action.options[String(duration)] ?? [];
}

const dayKey = (iso: string) => {
	const date = new Date(iso);
	return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

/** The options grouped by the reader's local day, in order. */
export function optionsByDay(
	options: ReadonlyArray<MeetingOption>,
): Array<{day: string; options: Array<MeetingOption>}> {
	const days: Array<{day: string; options: Array<MeetingOption>}> = [];
	for (const option of options) {
		const key = dayKey(option.start);
		const last = days.at(-1);
		if (last && last.day === key) last.options.push(option);
		else days.push({day: key, options: [option]});
	}
	return days;
}

/** The first time free for everyone, and a fallback on another day (else the next free time). */
export function bestAndBackup(
	options: ReadonlyArray<MeetingOption>,
	after?: string | null,
): {
	best: MeetingOption | null;
	backup: MeetingOption | null;
} {
	const free = options.filter((option) => option.busy.length === 0);
	const best = (after ? free.find((option) => option.start === after) : null) ?? free[0] ?? null;
	if (!best) return {best: null, backup: null};
	return {best, backup: backupFor(free, best)};
}

function backupFor(free: ReadonlyArray<MeetingOption>, best: MeetingOption): MeetingOption | null {
	return (
		free.find((option) => dayKey(option.start) !== dayKey(best.start)) ??
		free.find((option) => Date.parse(option.start) >= Date.parse(best.end)) ??
		free.find((option) => option.start !== best.start) ??
		null
	);
}

/** The choice the card starts with: the suggested length and its best time. */
export function initialChoice(action: MailMeetingAction): MeetingChoice {
	const duration = action.durations.includes(action.duration_minutes)
		? action.duration_minutes
		: (action.durations[0] ?? 60);
	const {best, backup} = bestAndBackup(optionsFor(action, duration));
	// Accepting one of the sender's own times is a confirmation, so the invite can go with it.
	return {duration, start: best?.start ?? null, backup: backup?.start ?? null, invite: action.offered && best != null};
}

/** A new time (and its fallback) for a length, keeping the start when that length offers it. */
export function chooseTime(action: MailMeetingAction, choice: MeetingChoice, start: string): MeetingChoice {
	const options = optionsFor(action, choice.duration);
	const picked = options.find((option) => option.start === start);
	if (!picked) return choice;
	const free = options.filter((option) => option.busy.length === 0);
	// The fallback stays while it is free and on another day; otherwise one is found again.
	const keep =
		choice.backup && dayKey(choice.backup) !== dayKey(start) && free.some((option) => option.start === choice.backup);
	const backup = keep
		? choice.backup
		: (backupFor(
				free.filter((option) => option.start !== start),
				picked,
			)?.start ?? null);
	return {...choice, start, backup};
}

export function chooseDuration(action: MailMeetingAction, choice: MeetingChoice, duration: number): MeetingChoice {
	const options = optionsFor(action, duration);
	const keep = options.find((option) => option.start === choice.start && option.busy.length === 0);
	const {best, backup} = bestAndBackup(options, keep?.start ?? null);
	const keepBackup = options.find((option) => option.start === choice.backup && option.busy.length === 0);
	return {
		...choice,
		duration,
		start: best?.start ?? null,
		backup: keepBackup && keepBackup.start !== best?.start ? keepBackup.start : (backup?.start ?? null),
	};
}

export function chooseBackup(action: MailMeetingAction, choice: MeetingChoice, start: string): MeetingChoice {
	if (start === choice.start) return choice;
	return optionsFor(action, choice.duration).some((option) => option.start === start)
		? {...choice, backup: start}
		: choice;
}

export function optionAt(action: MailMeetingAction, duration: number, start: string | null): MeetingOption | null {
	if (!start) return null;
	return optionsFor(action, duration).find((option) => option.start === start) ?? null;
}

// ── Text ─────────────────────────────────────────────────────────────────────

/** "09:00" where the clock is 24-hour, "9:00 AM" (no leading zero) where it is 12-hour. */
function hourStyle(locale: string): '2-digit' | 'numeric' {
	try {
		const cycle = new Intl.DateTimeFormat(locale, {hour: 'numeric'}).resolvedOptions().hourCycle;
		return cycle === 'h12' || cycle === 'h11' ? 'numeric' : '2-digit';
	} catch {
		return '2-digit';
	}
}

/**
 * Locale for the reply's times: the mail's language, and British English for English mail, as the
 * team writes to clients ("Tuesday 27 October"), whatever language the app itself is in.
 */
export function replyLocale(lang: string): string {
	return lang === 'en' ? 'en-GB' : lang;
}

/** "Tuesday 27 October, 10:00–11:30", as the reply says it: always the 24-hour clock. */
export function replyTimeText(option: {start: string; end: string}, locale: string): string {
	try {
		return new Intl.DateTimeFormat(locale, {
			weekday: 'long',
			day: 'numeric',
			month: 'long',
			hour: '2-digit',
			minute: '2-digit',
			hourCycle: 'h23',
		}).formatRange(new Date(option.start), new Date(option.end));
	} catch {
		return new Date(option.start).toLocaleString();
	}
}

/** "Tue 10:00", for buttons. */
export function shortTimeText(iso: string, locale: string): string {
	const date = new Date(iso);
	const day = new Intl.DateTimeFormat(locale, {weekday: 'short'}).format(date);
	const time = new Intl.DateTimeFormat(locale, {hour: hourStyle(locale), minute: '2-digit'}).format(date);
	return `${day} ${time}`;
}

/** "Tue 27 Oct, 10:00–11:30", for the composer's When line. */
export function pillTimeText(option: {start: string; end: string}, locale: string): string {
	try {
		return new Intl.DateTimeFormat(locale, {
			weekday: 'short',
			day: 'numeric',
			month: 'short',
			hour: hourStyle(locale),
			minute: '2-digit',
		}).formatRange(new Date(option.start), new Date(option.end));
	} catch {
		return shortTimeText(option.start, locale);
	}
}

export function dayLabel(iso: string, locale: string): string {
	return new Intl.DateTimeFormat(locale, {weekday: 'short', day: 'numeric'}).format(new Date(iso));
}

export function chipTimeText(iso: string, locale: string): string {
	return new Intl.DateTimeFormat(locale, {hour: hourStyle(locale), minute: '2-digit'}).format(new Date(iso));
}

export function durationText(minutes: number, locale: string): string {
	const hours = minutes / 60;
	return minutes < 60
		? new Intl.NumberFormat(locale, {style: 'unit', unit: 'minute', unitDisplay: 'narrow'}).format(minutes)
		: new Intl.NumberFormat(locale, {
				style: 'unit',
				unit: 'hour',
				unitDisplay: 'narrow',
				maximumFractionDigits: 1,
			}).format(hours);
}

/** The reply with the chosen times written into its [[time]] / [[backup]] marks. */
export function fillReplyTemplate(template: string, times: {time: string | null; backup: string | null}): string {
	let body = template.replaceAll('[[time]]', times.time ?? '[day and time]');
	if (times.backup) return body.replaceAll('[[backup]]', times.backup);
	if (!body.includes('[[backup]]')) return body;
	body = body.replace(/\s+\p{L}{1,4}\s+\[\[backup\]\](?=[\s?!.,])/gu, '');
	return body
		.split('\n')
		.map((line) =>
			line.includes('[[backup]]')
				? (line.match(/[^.!?]+(?:[.!?]+|$)\s*/g) ?? [line])
						.filter((sentence) => !sentence.includes('[[backup]]'))
						.join('')
						.trim()
				: line,
		)
		.join('\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

/** The texts a choice writes into the reply. */
export function choiceTexts(
	action: MailMeetingAction,
	choice: MeetingChoice,
	locale: string,
): {time: string | null; backup: string | null} {
	const time = optionAt(action, choice.duration, choice.start);
	const backup = optionAt(action, choice.duration, choice.backup);
	return {time: time ? replyTimeText(time, locale) : null, backup: backup ? replyTimeText(backup, locale) : null};
}

function escapeHtml(text: string): string {
	return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * The composer body with one time text swapped for another, or null when the old text is no
 * longer there (the writer changed it). Only the first match changes.
 */
export function swapTimeText(bodyHtml: string, from: string, to: string): string | null {
	const old = escapeHtml(from);
	const index = bodyHtml.indexOf(old);
	if (index < 0) return null;
	return bodyHtml.slice(0, index) + escapeHtml(to) + bodyHtml.slice(index + old.length);
}

// ── Invite ───────────────────────────────────────────────────────────────────

function localDateTime(iso: string): string {
	const date = new Date(iso);
	const pad = (value: number) => String(value).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Sends the Teams invite for the chosen time to everyone the reply went to. */
export async function sendMeetingInvite(params: {
	meeting: ComposeMeeting;
	recipients: ReadonlyArray<KonvoMailAddress>;
	selfAddress: string | null;
	timezone: string;
}): Promise<boolean> {
	const option = optionAt(params.meeting.action, params.meeting.choice.duration, params.meeting.choice.start);
	if (!option) return false;
	const self = params.selfAddress?.toLowerCase() ?? null;
	const attendees = [
		...new Set(
			params.recipients
				.map((entry) => entry.address.trim().toLowerCase())
				.filter((address) => address && address !== self),
		),
	];
	try {
		const response = await http.post(Endpoints.KONVO_CALENDAR_EVENTS, {
			body: {
				subject: params.meeting.action.topic,
				start: localDateTime(option.start),
				end: localDateTime(option.end),
				is_all_day: false,
				timezone: params.timezone,
				location: '',
				body_text: '',
				attendees,
				optional_attendees: [],
				teams: true,
				konvo_public: false,
				konvo_channel_id: null,
				konvo_guild_id: null,
				konvo_channel_name: null,
				recurrence: null,
			},
			mode: 'silent',
		});
		return response.ok;
	} catch {
		return false;
	}
}
